import { useEffect, useRef, useState } from "react";
import { MAX_VOICE_SECONDS, pickAudioMime } from "./sources.js";

export type RecorderStatus = "idle" | "recording" | "denied" | "unsupported" | "failed";

/**
 * Запись голосового прямо в кабинете (`MediaRecorder`). Запись не длиннее `MAX_VOICE_SECONDS`: на пределе
 * останавливается сама. Микрофон освобождается сразу после остановки; запись живёт только в памяти страницы.
 */
export function useVoiceRecorder(onDone: (blob: Blob, seconds: number) => void) {
  const [status, setStatus] = useState<RecorderStatus>("idle");
  const [seconds, setSeconds] = useState(0);
  const recorder = useRef<MediaRecorder | null>(null);
  const timer = useRef<number | null>(null);
  /** Запрос микрофона уже идёт (двойное нажатие не открывает второй поток). */
  const starting = useRef(false);
  /** Страница ещё открыта: разрешение могли дать уже после ухода, тогда микрофон сразу отпускаем. */
  const mounted = useRef(true);
  const done = useRef(onDone);
  done.current = onDone;

  const supported = typeof window !== "undefined" && typeof window.MediaRecorder !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia);

  const clearTimer = () => {
    if (timer.current !== null) window.clearInterval(timer.current);
    timer.current = null;
  };

  const stop = () => {
    clearTimer();
    if (recorder.current && recorder.current.state !== "inactive") recorder.current.stop();
  };

  const start = async () => {
    if (!supported) {
      setStatus("unsupported");
      return;
    }
    if (starting.current || recorder.current) return;
    starting.current = true;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      starting.current = false;
      if (mounted.current) setStatus("denied");
      return;
    }
    starting.current = false;
    if (!mounted.current) {
      for (const track of stream.getTracks()) track.stop();
      return;
    }
    try {
      const mimeType = pickAudioMime((t) => MediaRecorder.isTypeSupported(t));
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      const chunks: Blob[] = [];
      const startedAt = Date.now();
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      rec.onstop = () => {
        for (const track of stream.getTracks()) track.stop();
        clearTimer();
        recorder.current = null;
        setStatus("idle");
        const blob = new Blob(chunks, { type: (rec.mimeType || mimeType || "audio/webm").split(";")[0] ?? "audio/webm" });
        const secs = Math.min(MAX_VOICE_SECONDS, Math.round((Date.now() - startedAt) / 1000));
        if (blob.size > 0 && secs >= 1) done.current(blob, secs);
      };
      recorder.current = rec;
      rec.start(1000);
      setSeconds(0);
      setStatus("recording");
      timer.current = window.setInterval(() => {
        const secs = Math.floor((Date.now() - startedAt) / 1000);
        setSeconds(secs);
        if (secs >= MAX_VOICE_SECONDS) stop();
      }, 250);
    } catch {
      for (const track of stream.getTracks()) track.stop();
      setStatus("failed");
    }
  };

  // Ушли со страницы во время записи: останавливаем и отпускаем микрофон.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timer.current !== null) window.clearInterval(timer.current);
      const rec = recorder.current;
      if (rec && rec.state !== "inactive") {
        rec.onstop = () => {
          for (const track of rec.stream.getTracks()) track.stop();
        };
        rec.stop();
      }
    };
  }, []);

  return { status, seconds, supported, start: () => void start(), stop };
}
